import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Button,
    TextField,
    MenuItem,
    IconButton,
} from '@mui/material';
import {
    People,
    AttachMoney,
    Campaign,
    TrendingUp,
    Edit,
    Delete,
    Search,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import ResponsiveTable from '../components/ResponsiveTable';

const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPlan, setFilterPlan] = useState('');

    useEffect(() => {
        fetchStats();
        fetchUsers();
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get(`${API_BASE_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search: searchTerm, plan: filterPlan },
            });
            setUsers(data.users);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching users:', error);
            setLoading(false);
        }
    };

    const handleSearch = () => {
        fetchUsers();
    };

    const handleUpdateSubscription = async (userId, newPlan) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/api/admin/users/${userId}/subscription`,
                { plan: newPlan },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchUsers();
            fetchStats();
        } catch (error) {
            console.error('Error updating subscription:', error);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_BASE_URL}/api/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchUsers();
            fetchStats();
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    const planColors = {
        free: '#757575',
        pro: '#1976d2',
        enterprise: '#9c27b0',
    };

    return (
        <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
            <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}
            >
                لوحة تحكم المدير
            </Typography>

            {/* Stats Cards */}
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 3, md: 4 } }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <People sx={{ color: 'primary.main', mr: 1 }} />
                                <Typography variant="h6">إجمالي المستخدمين</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.users.total || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Free: {stats?.users.free} | Pro: {stats?.users.pro} | Enterprise: {stats?.users.enterprise}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <AttachMoney sx={{ color: 'success.main', mr: 1 }} />
                                <Typography variant="h6">الإيرادات الشهرية</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                ${stats?.revenue.monthly || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                سنوياً: ${stats?.revenue.annual || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Campaign sx={{ color: 'warning.main', mr: 1 }} />
                                <Typography variant="h6">الحملات</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.campaigns.total || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                نشطة: {stats?.campaigns.active || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <TrendingUp sx={{ color: 'info.main', mr: 1 }} />
                                <Typography variant="h6">الرسائل المرسلة</Typography>
                            </Box>
                            <Typography variant="h3" fontWeight="bold">
                                {stats?.usage.totalMessages || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                حسابات: {stats?.usage.totalAccounts || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Users Management */}
            <Card>
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        إدارة المستخدمين
                    </Typography>

                    {/* Filters */}
                    <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                placeholder="بحث بالاسم أو البريد الإلكتروني"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton onClick={handleSearch}>
                                            <Search />
                                        </IconButton>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                select
                                label="تصفية حسب الخطة"
                                value={filterPlan}
                                onChange={(e) => setFilterPlan(e.target.value)}
                            >
                                <MenuItem value="">الكل</MenuItem>
                                <MenuItem value="free">Free</MenuItem>
                                <MenuItem value="pro">Pro</MenuItem>
                                <MenuItem value="enterprise">Enterprise</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button variant="contained" fullWidth onClick={handleSearch}>
                                بحث
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Users Table */}
                    <ResponsiveTable
                        headers={[
                            { key: 'username', label: 'الاسم' },
                            { key: 'email', label: 'البريد الإلكتروني' },
                            { key: 'plan', label: 'الخطة' },
                            { key: 'usage', label: 'الاستخدام' },
                            { key: 'date', label: 'تاريخ التسجيل' },
                            { key: 'actions', label: 'الإجراءات' },
                        ]}
                        rows={users}
                        desktopRowRenderer={(user) => (
                            <TableRow key={user._id}>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.subscription?.plan?.toUpperCase() || 'FREE'}
                                        sx={{
                                            bgcolor: planColors[user.subscription?.plan || 'free'],
                                            color: 'white',
                                            fontWeight: 'bold',
                                        }}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {user.usage?.messagesSent || 0} رسائل
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {user.usage?.campaignsCreated || 0} حملات
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {new Date(user.createdAt).toLocaleDateString('ar-EG')}
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <TextField
                                            select
                                            size="small"
                                            value={user.subscription?.plan || 'free'}
                                            onChange={(e) => handleUpdateSubscription(user._id, e.target.value)}
                                            sx={{ minWidth: 100, mr: 1 }}
                                        >
                                            <MenuItem value="free">Free</MenuItem>
                                            <MenuItem value="pro">Pro</MenuItem>
                                            <MenuItem value="enterprise">Enterprise</MenuItem>
                                        </TextField>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteUser(user._id)}
                                            size="small"
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                        mobileCardRenderer={(user) => (
                            <Card key={user._id} sx={{ mb: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Typography variant="h6" fontWeight="bold">
                                            {user.username}
                                        </Typography>
                                        <Chip
                                            label={user.subscription?.plan?.toUpperCase() || 'FREE'}
                                            sx={{
                                                bgcolor: planColors[user.subscription?.plan || 'free'],
                                                color: 'white',
                                                fontWeight: 'bold',
                                            }}
                                            size="small"
                                        />
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {user.email}
                                    </Typography>

                                    <Box sx={{ my: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                        <Typography variant="body2" fontWeight="bold">الاستخدام:</Typography>
                                        <Typography variant="body2">
                                            {user.usage?.messagesSent || 0} رسائل | {user.usage?.campaignsCreated || 0} حملات
                                        </Typography>
                                    </Box>

                                    <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                                        تاريخ التسجيل: {new Date(user.createdAt).toLocaleDateString('ar-EG')}
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2 }}>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label="تغيير الخطة"
                                            value={user.subscription?.plan || 'free'}
                                            onChange={(e) => handleUpdateSubscription(user._id, e.target.value)}
                                        >
                                            <MenuItem value="free">Free</MenuItem>
                                            <MenuItem value="pro">Pro</MenuItem>
                                            <MenuItem value="enterprise">Enterprise</MenuItem>
                                        </TextField>
                                        <IconButton
                                            color="error"
                                            onClick={() => handleDeleteUser(user._id)}
                                            sx={{ border: 1, borderColor: 'error.main', borderRadius: 1 }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    />
                </CardContent>
            </Card>
        </Container>
    );
};

export default AdminDashboard;
